import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { CouponFeedItem } from 'src/app/shared/models/coupon-feed-item/coupon-feed-item';
import { RewardRequestDTO } from 'src/app/shared/models/dtos/requests/reward-request-dto/reward-request-dto';
import { BusinessSummaryResponseDTO } from 'src/app/shared/models/dtos/responses/business-summary-response-dto/business-summary-response-dto';
import { Coupon } from 'src/app/shared/models/dtos/responses/coupon/coupon';
import { PointSummaryDTO } from 'src/app/shared/models/dtos/responses/point-summary/point-summary-dto';
import { ProductRewardsResponseDTO } from 'src/app/shared/models/dtos/responses/product-rewards-response-dto/ProductRewardsResponseDto';
import { RewardResponseDTO } from 'src/app/shared/models/dtos/responses/reward-response-dto/reward-response-dto';
import { SysadminMonthPointBalanceResponseDTO } from 'src/app/shared/models/dtos/responses/sysadmin-month-point-balance-response-dto/sysadmin-month-point-balance-response-dto';
import { toAPIDate } from 'src/app/utils/date';
import { TransactionGroup } from 'src/app/shared/models/transaction/transaction-group';
import {
  PageResponse,
  PortPointsExecution,
} from '../../../shared/models/migration-upload-file/migration-upload-file';
import { GenericResponseDTO } from '../../../shared/models/dtos/responses/message-response-dto/message-response-dto';

@Injectable({
  providedIn: 'root',
})
/**
 * Service to interact with LnR's Reward Service
 */
export class RewardService {
  private backendAuthURL = '/rwd/api/a';
  private backendURL = '/rwd/api';

  /**
   * @constructor
   */
  constructor(private httpClient: HttpClient) {}

  /**
   * @param {Object} queryParams
   * @return {Observable<CouponFeedItem[]>}
   */
  getCouponsFeed(queryParams: {
    page: number;
    pageSize: number;
    expired?: boolean;
    vip?: boolean;
    startDate?: Date;
    endDate?: Date;
  }): Observable<CouponFeedItem[]> {
    const endpoint = 'coupon/feed';
    const params: any = queryParams as any;
    return this.httpClient.get<CouponFeedItem[]>(this.buildURL(endpoint), {
      params,
    });
  }

  /**
   * Gets a summary of points of a given business and some business information
   * @param {number} businessId
   * @param {number} couponId
   * @return {Observable}
   */
  getBusinessInfo(
    businessId: number,
    couponId: number
  ): Observable<BusinessSummaryResponseDTO> {
    const endpoint = `point/business/info/${businessId}/${couponId}`;
    return this.httpClient.get<BusinessSummaryResponseDTO>(
      this.buildURL(endpoint)
    );
  }

  /**
   * @param {number} couponId
   * @param {string} note
   * @return {Observable}
   */
  putNoteCoupon(couponId: number, note: string): Observable<any> {
    const endpoint = `coupon/note/${couponId}`;
    return this.httpClient.put<any>(this.buildURL(endpoint), { note: note });
  }

  /**
   * Return the total of points used in the coupon that isn't expired yet
   * and the points that are already expired.
   * @param {number} couponId
   * @return {Observable}
   */
  getTotalPointsUsedInCoupon(couponId: number) {
    const endpoint = `point/summary/coupon/${couponId}`;
    return this.httpClient.get<any>(this.buildURL(endpoint));
  }

  /**
   * Sets coupon status to redeemed
   * @param {number} couponId
   * @param {string} invoice
   * @param {string} reseller
   * @param {number} discount
   * @param {Date} date
   * @return {Observable}
   */
  setCouponRedeemed(
    couponId: number,
    invoice: string,
    reseller: string,
    discount: number,
    date: Date
  ) {
    const endpoint = `coupon/status/redeemed/${couponId}`;
    return this.httpClient.put<any>(this.buildURL(endpoint), {
      invoice: invoice,
      reseller: reseller,
      discountValueUSD: discount,
      redemptionDate: date,
    });
  }

  /**
   * Updates coupon redeemd information
   * @param {number} couponId
   * @param {string} invoice
   * @param {string} reseller
   * @param {number} discount
   * @param {Date} date
   * @return {Observable}
   */
  updateCouponRedeemedInfo(
    couponId: number,
    invoice: string,
    reseller: string,
    discount: number,
    date: Date
  ) {
    const endpoint = `coupon/redeemed/${couponId}`;
    return this.httpClient.put<any>(this.buildURL(endpoint), {
      invoice: invoice,
      reseller: reseller,
      discountValueUSD: discount,
      redemptionDate: date,
    });
  }

  /**
   * Sets coupon status to canceled
   * @param {number} couponId
   * @return {Observable}
   */
  setCouponCanceled(couponId: number) {
    const endpoint = `point/coupon/cancel/${couponId}`;
    return this.httpClient.put<any>(this.buildURL(endpoint), {});
  }

  /**
   * Returns a brief summary of point movement in a business
   * for the current month
   * @param {number} businessId
   * @return {Observable<PointSummaryDTO>}
   */
  getBusinessPointSummary(businessId: number): Observable<PointSummaryDTO> {
    const endpoint = `point/business/summary/${businessId}`;
    return this.httpClient.get<PointSummaryDTO>(this.buildURL(endpoint));
  }

  /**
   * Returns a brief summary of point movement in a business
   * for the current month
   * @param {number} businessId
   * @param {any} familyId
   * @return {Observable<PointSummaryDTO>}
   */
  getBusinessPointSummaryByFamily(
    businessId: number,
    familyId: any
  ): Observable<PointSummaryDTO> {
    const endpoint = `point/business/summary/${businessId}/family/${familyId}`;
    return this.httpClient.get<PointSummaryDTO>(this.buildURL(endpoint));
  }

  /**
   * Returns paginated rewards
   * @param {TGetRewardsQueryParams} queryParams
   * @return {Observable<RewardResponseDTO[]>}
   */
  getRewards(
    queryParams: TGetRewardsQueryParams
  ): Observable<RewardResponseDTO[]> {
    const endpoint = 'reward/all';
    return this.httpClient.get<RewardResponseDTO[]>(this.buildURL(endpoint), {
      params: queryParams,
    });
  }

  /**
   * Get a reward by passing the id
   * @param {number} id
   * @return {Observable<RewardResponseDTO>}
   */
  getReward(id: number): Observable<RewardResponseDTO> {
    const endpoint = `sysadmin/reward/${id}`;
    return this.httpClient.get<RewardResponseDTO>(this.buildURL(endpoint));
  }

  /**
   * Disables a reward by passing the id
   * @param {number} id
   * @return {Observable<RewardResponseDTO>}
   */
  disableReward(id: number): Observable<RewardResponseDTO> {
    const endpoint = `reward/disable/${id}`;
    return this.httpClient.get<RewardResponseDTO>(this.buildURL(endpoint));
  }

  /**
   * Disables a reward by passing the id
   * @param {number} id
   * @return {Observable<RewardResponseDTO>}
   */
  enableReward(id: number): Observable<RewardResponseDTO> {
    const endpoint = `reward/enable/${id}`;
    return this.httpClient.get<RewardResponseDTO>(this.buildURL(endpoint));
  }

  /**
   * Deletes a reward by passing the id
   * @param {number} id
   * @return {Observable<RewardResponseDTO>}
   */
  deleteReward(id: number): Observable<RewardResponseDTO> {
    const endpoint = `reward/${id}`;
    return this.httpClient.delete<RewardResponseDTO>(this.buildURL(endpoint));
  }

  /**
   * Post request to save a new reward
   * @param {RewardRequestDTO} reward
   * @return {Observable<RewardRequestDTO>}
   */
  saveReward(reward: RewardRequestDTO): Observable<RewardRequestDTO> {
    const endpoint = 'reward';
    return this.httpClient.post<RewardRequestDTO>(
      this.buildURL(endpoint),
      reward
    );
  }

  /**
   * Put request to edit a new reward
   * @param {number} rewardId
   * @param {RewardRequestDTO} reward
   * @return {Observable<RewardRequestDTO>}
   */
  editReward(
    rewardId: number,
    reward: RewardRequestDTO
  ): Observable<RewardRequestDTO> {
    const endpoint = `reward/${rewardId}`;
    return this.httpClient.put<RewardRequestDTO>(
      this.buildURL(endpoint),
      reward
    );
  }

  /**
   * @param {number} businessId
   * @param {Object} queryParams
   * @return {Observable<Transaction[]>}
   */
  getTransactions(
    businessId: number,
    queryParams: {
      page: number;
      pageSize: number;
      spend?: boolean;
      added?: boolean;
      expired?: boolean;
      startDate?: Date;
      endDate?: Date;
    }
  ): Observable<TransactionGroup[]> {
    const endpoint = `transaction/business/${businessId}`;
    const params: any = queryParams as any;
    for (const param in params) {
      if (params[param] == null) {
        delete params[param];
      }
    }
    return this.httpClient
      .get<TransactionGroup[]>(this.buildURL(endpoint), {
        params,
      })
      .pipe(
        map((groups) =>
          groups.map((group) => {
            group.date = new Date(group.date + 'T00:00:00');
            return group;
          })
        )
      );
  }

  /**
   * @param {number} businessId
   * @param {number} familyId
   * @param {Object} filters
   * @return {Observable<string>}
   */
  createReport(
    businessId: number,
    familyId: number,
    filters: {
      includeRedeemed?: boolean;
      includeAdded?: boolean;
      includeExpired?: boolean;
      includeRemoved?: boolean;
      startDate?: Date;
      endDate?: Date;
      familyId?: number;
    }
  ): Observable<string> {
    const startDate = filters.startDate
      ? toAPIDate(filters.startDate)
      : undefined;
    const endDate = filters.endDate ? toAPIDate(filters.endDate) : undefined;
    const endpoint = `transaction/report/${businessId}`;
    const params: any = {
      spend: filters.includeRedeemed,
      added: filters.includeAdded,
      expired: filters.includeExpired,
      removed: filters.includeRemoved,
      familyId: familyId,
    };
    startDate && (params['startDate'] = startDate);
    endDate && (params['endDate'] = endDate);
    return this.httpClient.get(this.buildURL(endpoint), {
      params,
      responseType: 'text',
    });
  }

  /**
   * @param {number} businessId
   * @param {number} transactionValue
   * @param {string} note
   * @param {number} familyId
   * @return {any}
   */
  createPointsTransaction(
    businessId: number,
    transactionValue: number,
    note: string,
    familyId: number
  ): Observable<any> {
    const endpoint = 'transaction/create';

    return this.httpClient.post<any>(this.buildURL(endpoint), {
      businessId: businessId,
      transactionValue: transactionValue,
      note: note,
      familyId: familyId,
    });
  }

  /**
   * Remove transaction points of a given business
   * @param {number} businessId
   * @param {number} transactionValue
   * @param {string} note
   * @param {number} familyId
   * @return {any}
   */
  removePointsTransaction(
    businessId: number,
    transactionValue: number,
    note: string,
    familyId: number
  ): Observable<any> {
    const endpoint = `point/remove/${businessId}`;

    return this.httpClient.post<any>(this.buildURL(endpoint), {
      pointsToBeRemoved: transactionValue,
      note: note,
      familyId: familyId,
    });
  }

  /**
   * @param {number} businessId
   * @param {any} queryParams
   * @return {Observable<Coupon[]>}
   */
  getCoupons(businessId: number, queryParams: any): Observable<Coupon[]> {
    const endpoint = `coupon/business/${businessId}`;
    const params: any = queryParams as any;
    return this.httpClient.get<Coupon[]>(this.buildURL(endpoint), {
      params,
    });
  }

  /**
   * Gets a reward service API version
   * @return {Observable<string>}
   */
  getApiVersion(): Observable<string> {
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );
    return this.httpClient.get(`${this.backendURL}/version`, {
      headers,
      responseType: 'text',
    });
  }

  /**
   * Return the total remaining points available in the current month to spend system-wide and for the business given.
   * @param {number} businessId
   * @param {number} familyId
   * @return {Observable<SysadminMonthPointBalanceResponseDTO>}
   */
  getSysadminMonthPointBalance(businessId: number, familyId: number) {
    const endpoint = `transaction/points-balance/business/${businessId}/family/${familyId}`;
    return this.httpClient.get<SysadminMonthPointBalanceResponseDTO>(
      this.buildURL(endpoint)
    );
  }

  /**
   * Return the list of rewards of the product
   * @param {number} productId
   * @param {number} page
   * @param {number} pageSize
   * @return {Observable<ProductRewardsResponseDTO>}
   */
  getRewardsByProductId(
    productId: number,
    page: number,
    pageSize: number
  ): Observable<ProductRewardsResponseDTO[]> {
    const endpoint = `reward/product/${productId}`;
    return this.httpClient.get<ProductRewardsResponseDTO[]>(
      this.buildURL(endpoint),
      {
        params: {
          page,
          pageSize,
        },
      }
    );
  }

  /**
   * Gets paginated PORT points batch executions
   * @param {number} page
   * @param {number} pageSize
   * @return {Observable<PageResponse<PortPointsExecution>>}
   */
  getPortPointsExecutions(
    page: number,
    pageSize: number
  ): Observable<PageResponse<PortPointsExecution>> {
    const endpoint = 'port-points/executions';

    return this.httpClient.get<PageResponse<PortPointsExecution>>(
      this.buildURL(endpoint),
      {
        params: {
          page,
          pageSize,
        },
      }
    );
  }

  /**
   * Starts PORT points processing
   * @return {Observable<GenericResponseDTO>}
   */
  startPortPointsProcessing(): Observable<GenericResponseDTO> {
    const endpoint = 'point/port-points';

    return this.httpClient.post<GenericResponseDTO>(
      this.buildURL(endpoint),
      {}
    );
  }

  /**
   * Builds URL from endpoint
   * @param {string} endpoint
   * @return {string}
   */
  private buildURL(endpoint: string): string {
    return `${this.backendAuthURL}/${endpoint}`;
  }
}

export type TGetRewardsQueryParams = {
  page: number;
  pageSize: number;
  sort: string;
  cservice: boolean;
  products: boolean;
  familyIds?: string;
  regionId?: number;
};
